import { ReactNode, useEffect, useMemo, useState } from "react";
import {
  UserInfoDto,
  instance,
  registerAuthErrorCallback,
} from "@semo/api-contracts";
import { VendorAuthContext } from "./VendorAuthContext";
import { useEmbedAuth } from "../hooks/useEmbedAuth";
import { clearEmbedSession } from "../services/embedSession";
import {
  EmbedLoadingState,
  EmbedErrorState,
  EmbedUnauthorizedState,
} from "../../presentation/components/EmbedAuthStates";
import {
  EMBED_SESSION_EXPIRED_NOTICE,
  toEmbedAuthNotice,
} from "../../presentation/embedAuthNotice";
import { isDashboardEnabled } from "../../../../utils/dashboardMode";

/** 응답 오류가 401(인증 만료·무효)인가 — axios 오류 모양만 본다. */
function isUnauthorizedResponse(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  if (!("response" in error)) return false;
  const { response } = error;
  if (typeof response !== "object" || response === null) return false;
  return "status" in response && response.status === 401;
}

/** 오류·만료 화면에서 빠져나갈 길 — 로그인 화면이 있는 빌드면 «로그인 화면으로», 팝업이면 «창 닫기». */
function exitActions(): { label: string; onClick: () => void }[] {
  const actions: { label: string; onClick: () => void }[] = [];
  if (isDashboardEnabled()) {
    actions.push({
      label: "로그인 화면으로",
      onClick: () => window.location.assign("/login"),
    });
  }
  if (window.opener) {
    actions.push({ label: "창 닫기", onClick: () => window.close() });
  }
  return actions;
}

interface EmbedAuthProviderProps {
  children: ReactNode;
}

/**
 * EmbedAuthProvider
 *
 * Provides VendorAuthContext for embed pages using token-based authentication.
 * Handles the `useEmbedAuth` verification flow and exposes the result via context.
 *
 * This replaces the need for individual pages to handle auth states *if* they rely on components
 * that require VendorAuthContext (like VendorChatWindow).
 */
export function EmbedAuthProvider({ children }: EmbedAuthProviderProps) {
  const {
    isAuthenticated,
    isVerifying,
    error,
    retry,
    authPayload,
    supplierInfo,
  } = useEmbedAuth();

  // Convert Embed payload to UserInfoDto-like structure for the context
  const currentUser: UserInfoDto | null = useMemo(() => {
    if (!isAuthenticated || !authPayload?.userId) return null;

    return {
      id: authPayload.userId,
      contactEmail: supplierInfo?.contactEmail || "",
      name: supplierInfo?.managerName || supplierInfo?.companyName || "공급사",
      userType: "supplier",
    } as UserInfoDto;
  }, [isAuthenticated, authPayload, supplierInfo]);

  // 판매자 센터를 열어 둔 채 세션이 만료되면 모든 조회가 401 인데 화면마다 «불러오는 중 오류»·
  // «0건» 이 정상처럼 보였다(A3-15). 인증된 뒤 401 이 한 번이라도 오면 만료 안내로 바꾼다.
  //  - /embed/* 경로: 공용 API 도우미가 401 을 그대로 넘겨 준다 → 응답 인터셉터로 잡는다.
  //  - 그 밖의 임베드 경로(/settlements·/chat/… 등): 공용 도우미가 갱신을 시도하다 실패하면
  //    인증 오류 콜백을 부른다 → 콜백으로 잡는다.
  const [sessionExpired, setSessionExpired] = useState(false);
  useEffect(() => {
    if (!isAuthenticated) return;
    const markExpired = () => {
      clearEmbedSession();
      setSessionExpired(true);
    };
    const interceptorId = instance.interceptors.response.use(
      undefined,
      (error: unknown) => {
        if (isUnauthorizedResponse(error)) markExpired();
        return Promise.reject(
          error instanceof Error ? error : new Error(String(error)),
        );
      },
    );
    const unsubscribe = registerAuthErrorCallback(markExpired);
    return () => {
      instance.interceptors.response.eject(interceptorId);
      unsubscribe();
    };
  }, [isAuthenticated]);

  // Context value
  const contextValue = {
    currentUser,
    supplierInfo: supplierInfo ?? null,
    isLoading: isVerifying,
    isSessionLoading: isVerifying,
    // Embed users don't have a traditional "logout" or "checkSession"
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    logout: async () => {},
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    setCurrentUser: () => {},
    checkSession: () => retry(), // Retry verification on checkSession
  };

  // Render Auth States directly here (Layout Wrapper behavior)
  if (isVerifying) {
    return <EmbedLoadingState message="인증 확인 중..." />;
  }

  if (sessionExpired) {
    return (
      <EmbedErrorState
        title={EMBED_SESSION_EXPIRED_NOTICE.title}
        message={EMBED_SESSION_EXPIRED_NOTICE.message}
        actions={exitActions()}
      />
    );
  }

  if (error) {
    const notice = toEmbedAuthNotice(error);
    return (
      <EmbedErrorState
        title={notice.title}
        message={notice.message}
        onRetry={notice.retryable ? retry : undefined}
        actions={exitActions()}
      />
    );
  }

  if (!isAuthenticated) {
    return <EmbedUnauthorizedState />;
  }

  return (
    <VendorAuthContext.Provider value={contextValue}>
      {children}
    </VendorAuthContext.Provider>
  );
}
